import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Mail, MessageCircle, Save, Info, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const { token, user } = useAuth();

  useEffect(() => {
    fetchPreferences();
  }, [token]);

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email_opt_in: preferences.email_opt_in,
          whatsapp_opt_in: preferences.whatsapp_opt_in,
          transactional_only: preferences.transactional_only
        })
      });

      if (res.ok) {
        toast.success('Preferenze salvate');
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <PageHeader
        title="Preferenze Notifiche"
        subtitle="Le tue preferenze di notifica"
      />

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Le notifiche di sistema importanti (scadenze critiche, sicurezza) vengono sempre inviate.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Notifiche Email
          </CardTitle>
          <CardDescription>
            Ricevi aggiornamenti importanti via email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Email transazionali</Label>
              <p className="text-sm text-gray-500">
                Ricevi promemoria pagamenti, convocazioni assemblee, nuovi documenti
              </p>
            </div>
            <Switch
              checked={preferences?.email_opt_in ?? true}
              onCheckedChange={(checked) => 
                setPreferences({...preferences, email_opt_in: checked})
              }
            />
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            <Check className="h-4 w-4 text-green-500" />
            <span>Email: <strong>{user?.email}</strong></span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            Notifiche WhatsApp
          </CardTitle>
          <CardDescription>
            Ricevi messaggi rapidi su WhatsApp (opzionale)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Abilita WhatsApp</Label>
              <p className="text-sm text-gray-500">
                Ricevi link diretti WhatsApp per comunicazioni importanti
              </p>
            </div>
            <Switch
              checked={preferences?.whatsapp_opt_in ?? false}
              onCheckedChange={(checked) => 
                setPreferences({...preferences, whatsapp_opt_in: checked})
              }
            />
          </div>

          {preferences?.whatsapp_opt_in && (
            <Alert className="border-green-200 bg-green-50">
              <MessageCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Quando disponibile, l&apos;amministratore potrà inviarti messaggi WhatsApp 
                per comunicazioni urgenti.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tipologia notifiche</CardTitle>
          <CardDescription>
            Scegli quali tipi di notifiche ricevere
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">Solo notifiche transazionali</Label>
              <p className="text-sm text-gray-500">
                Ricevi solo notifiche relative al tuo condominio (pagamenti, assemblee, documenti)
              </p>
            </div>
            <Switch
              checked={preferences?.transactional_only ?? true}
              onCheckedChange={(checked) => 
                setPreferences({...preferences, transactional_only: checked})
              }
            />
          </div>

          <Separator />

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p className="font-medium text-sm">Riceverai sempre:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-green-500" />
                Promemoria scadenze rate
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-green-500" />
                Convocazioni assemblee
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-green-500" />
                Nuovi documenti pubblicati
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-3 w-3 text-green-500" />
                Comunicazioni dall&apos;amministratore
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={savePreferences} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Salvando...' : 'Salva Preferenze'}
        </Button>
      </div>
    </div>
  );
}
